import {
  SYSTEM_ACTION_PLAN_CATEGORY,
  SYSTEM_ACTION_PLAN_NOTES,
  SYSTEM_ACTION_PLAN_SYSTEM,
} from '../../../../util/fhir';
import _cloneDeep from 'lodash/cloneDeep';
import {
  Apple,
  BoneJoint,
  Brain,
  Calendar,
  CloudLightning,
  ExternalLink,
  Fire,
  Heart,
  Image,
  Liver2,
  Microscope,
  Moon,
  Pill,
  Plant,
  Ribbon,
  Running,
  Shield,
  TestTube,
  Thyroid,
} from '@lifeomic/chromicons';

export const ACTION_PLAN_CATEGORIES = [
  'lifestyle',
  'sleep',
  'medication',
  'supplement',
  'nutrition',
  'exercise',
  'referral',
  'follow-up',
  'additional-testing',
  'additional-imaging',
] as const;

export const ACTION_PLAN_CATEGORIES_ICONS: Record<
  (typeof ACTION_PLAN_CATEGORIES)[number],
  typeof Plant
> = {
  lifestyle: Plant,
  sleep: Moon,
  medication: Pill,
  supplement: Pill,
  nutrition: Apple,
  exercise: Running,
  referral: ExternalLink,
  'follow-up': Calendar,
  'additional-testing': TestTube,
  'additional-imaging': Image,
};

// the order of these systems is important, as it determines the order of the systems in the presentation
export const ACTION_PLAN_SYSTEMS = [
  'heart',
  'cancer',
  'brain',
  'musculoskeletal',
  'metabolic',
  'general', // used for any that don't have a system set, as system was added later
  'supplements', // this is out of place here but it's to let them give a single high level "supplements" item
  'cardiometabolic',
  'immune',
  'hormones',
  'energy',
  'detoxification',
  'gastrointestinal',
] as const;

export const ACTION_PLAN_SYSTEMS_ICONS: Record<
  (typeof ACTION_PLAN_SYSTEMS)[number],
  typeof Heart
> = {
  cardiometabolic: Heart,
  general: Plant,
  immune: Shield,
  hormones: Thyroid,
  energy: CloudLightning,
  detoxification: Liver2,
  gastrointestinal: Microscope,
  heart: Heart,
  cancer: Ribbon,
  brain: Brain,
  musculoskeletal: BoneJoint,
  metabolic: Fire,
  supplements: Pill,
};

export const emptyActionItem = (): fhir.CarePlanActivity => ({
  detail: {
    status: 'unknown',
    category: {
      coding: [],
    },
    description: '',
    extension: [],
    reasonReference: [],
  },
});

export class ActionPlanActivity {
  activity: fhir.CarePlanActivity;
  constructor(activity?: fhir.CarePlanActivity) {
    this.activity = activity || emptyActionItem();
  }

  getDescription(): string | undefined {
    return this.activity?.detail?.extension?.find(
      ext => ext.url === SYSTEM_ACTION_PLAN_NOTES,
    )?.valueString;
  }

  setDescription(notes: string) {
    const existing: number =
      this.activity?.detail?.extension?.findIndex(
        ext => ext.url === SYSTEM_ACTION_PLAN_NOTES,
      ) ?? -1;

    if (existing !== -1) {
      this.activity.detail!.extension![existing].valueString = notes;
    } else {
      this.activity!.detail!.extension = [
        ...(this.activity?.detail?.extension || []),
        {
          url: SYSTEM_ACTION_PLAN_NOTES,
          valueString: notes,
        },
      ];
    }
  }

  getTitle(): string | undefined {
    return this.activity.detail?.description; // Description is the short form title. The actual description is in the detail extension
  }

  setTitle(title: string) {
    this.activity.detail!.description = title;
  }

  addKeyFinding(finding: string) {
    // Make sure we don't add duplicates
    const existing = new Set(this.getKeyFindings().map(f => f.toLowerCase()));
    if (existing.has(finding.toLowerCase())) return;

    this.activity.detail!.reasonReference = [
      ...(this.activity.detail!.reasonReference || []),
      {
        display: finding,
      },
    ];
  }

  removeKeyFinding(finding: string) {
    this.activity.detail!.reasonReference =
      this.activity.detail!.reasonReference?.filter(
        ref => ref.display !== finding,
      );
  }

  getKeyFindings(): string[] {
    return (this.activity.detail?.reasonReference || [])
      .map(ref => ref?.display)
      .filter((d): d is string => !!d);
  }

  setCategory(category: string) {
    const idx =
      this.activity.detail!.category?.coding?.findIndex(
        c => c.system === SYSTEM_ACTION_PLAN_CATEGORY,
      ) ?? -1;

    const cat = {
      system: SYSTEM_ACTION_PLAN_CATEGORY,
      code: category,
    };

    if (idx !== -1) {
      this.activity.detail!.category!.coding![idx] = cat;
    } else {
      this.activity.detail!.category = {
        coding: [...(this.activity.detail!.category?.coding || []), cat],
      };
    }
  }

  getCategory(): string | undefined {
    return this.activity.detail?.category?.coding?.find(
      c => c.system === SYSTEM_ACTION_PLAN_CATEGORY,
    )?.code;
  }

  getSystem(): string | undefined {
    return this.activity.detail?.extension?.find(
      ext => ext.url === SYSTEM_ACTION_PLAN_SYSTEM,
    )?.valueString;
  }

  setSystem(system: string) {
    const existing: number =
      this.activity?.detail?.extension?.findIndex(
        ext => ext.url === SYSTEM_ACTION_PLAN_SYSTEM,
      ) ?? -1;

    if (existing !== -1) {
      this.activity.detail!.extension![existing].valueString = system;
    } else {
      this.activity.detail!.extension = [
        ...(this.activity.detail?.extension || []),
        {
          url: SYSTEM_ACTION_PLAN_SYSTEM,
          valueString: system,
        },
      ];
    }
  }

  copy() {
    return new ActionPlanActivity(_cloneDeep(this.activity));
  }
}
